import { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

const GET_BRAND_DASHBOARD_CAMPAIGN_CERTIFICATIONS = gql`
  query BrandDashboardCampaignCertifications($campaignId: Int!) {
    campaign(campaignId: $campaignId) {
      id
      productName
      productImageUrl
      channel
      productUrl
      isPublished
      originalPrice
      discountPrice
      paybackPrice
      discountRate
      brand {
        id
        name
      }
      steps {
        id
        stepOrder
        type
        title
      }
      runs {
        id
        startAt
        endAt
        isActive
        participationCount
        participations {
          id
          userId
          status
          joinedAt
          user {
            id
            uuid
            name
            phone
            area1
            area2
            area3
            area4
          }
          submissions {
            id
            stepOrder
            status
            isSubmitted
            isApproved
            proofData
            step {
              id
              stepOrder
              type
            }
          }
        }
      }
    }
  }
`;

type BrandDashboardCampaignCertificationsVars = {
  campaignId: number;
};

type BrandDashboardCampaignCertificationsData = {
  campaign?: {
    id: number;
    productName: string;
    productImageUrl?: string | null;
    channel: string;
    productUrl?: string | null;
    isPublished: boolean;
    originalPrice: number;
    discountPrice: number;
    paybackPrice: number;
    discountRate?: number | null;
    brand: {
      id: number;
      name: string;
    };
    steps: Array<{
      id: number;
      stepOrder: number;
      type: string;
      title?: string | null;
    }>;
    runs: Array<{
      id: number;
      startAt: string;
      endAt: string;
      isActive: boolean;
      participationCount: number;
      participations: Array<{
        id: number;
        userId: number;
        status: string;
        joinedAt: string;
        user: {
          id: number;
          uuid?: string | null;
          name?: string | null;
          phone?: string | null;
          area1?: string | null;
          area2?: string | null;
          area3?: string | null;
          area4?: string | null;
        };
        submissions: Array<{
          id: number;
          stepOrder: number;
          status: string;
          isSubmitted: boolean;
          isApproved: boolean;
          proofData?: unknown | null;
          step?: {
            id: number;
            stepOrder: number;
            type: string;
          } | null;
        }>;
      }>;
    }>;
  } | null;
};

const extractImageUrls = (proofData: unknown): string[] => {
  if (!proofData) return [];

  if (typeof proofData === "string") {
    try {
      return extractImageUrls(JSON.parse(proofData));
    } catch {
      return [proofData];
    }
  }

  if (Array.isArray(proofData)) {
    return proofData.flatMap((item) => extractImageUrls(item));
  }

  if (typeof proofData === "object") {
    const obj = proofData as Record<string, unknown>;
    const candidates: unknown[] = [obj.imageUrl, obj.imageUrls, obj.images, obj.url, obj.urls];
    return candidates.flatMap((c) => extractImageUrls(c));
  }

  return [];
};

const BrandDashboardCampaignDetail = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const { data, loading, error } = useQuery<
    BrandDashboardCampaignCertificationsData,
    BrandDashboardCampaignCertificationsVars
  >(GET_BRAND_DASHBOARD_CAMPAIGN_CERTIFICATIONS, {
    variables: { campaignId: parseInt(campaignId || "0") },
    skip: !campaignId,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">
          에러가 발생했습니다: {error?.message || "캠페인을 찾을 수 없습니다."}
        </div>
      </div>
    );
  }

  const campaign = data.campaign;
  const runs = campaign.runs || [];
  const steps = campaign.steps || [];
  const sortedSteps = [...steps].sort((a, b) => a.stepOrder - b.stepOrder);
  const totalParticipants = runs.reduce((sum, run) => sum + (run.participationCount || 0), 0);
  const runCount = runs.length;
  const activeRunCount = runs.filter((r) => r.isActive).length;
  const firstRunStartAt =
    runCount > 0 ? new Date(Math.min(...runs.map((r) => new Date(r.startAt).getTime()))) : null;
  const lastRunEndAt =
    runCount > 0 ? new Date(Math.max(...runs.map((r) => new Date(r.endAt).getTime()))) : null;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });
  };
  const formatDateWithWeekday = (date: Date) => {
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      weekday: "short",
    });
  };

  const sortedRuns = [...runs].sort(
    (a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime()
  );
  const activeSortedRuns = sortedRuns.filter((r) => r.isActive);

  // 참여자 수집 + 유저별 그룹핑 (같은 유저가 여러 run에 참여할 수 있음)
  const allParticipations = runs.flatMap((run) => run.participations || []);
  const userMap = new Map<
    number,
    { user: (typeof allParticipations)[number]["user"]; participations: (typeof allParticipations)[number][] }
  >();

  allParticipations.forEach((participation) => {
    const userId = participation.userId;
    if (!userMap.has(userId)) {
      userMap.set(userId, { user: participation.user, participations: [] });
    }
    userMap.get(userId)!.participations.push(participation);
  });

  const users = Array.from(userMap.values());

  const formatAddress = (user: (typeof users)[number]["user"]) => {
    const parts = [user.area1, user.area2, user.area3, user.area4].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "-";
  };

  const renderCertificationStatus = (
    participation: (typeof allParticipations)[number],
    step: (typeof sortedSteps)[number]
  ) => {
    const submission = participation.submissions.find((s) => s.step?.id === step.id);

    if (!submission) {
      return (
        <td key={step.id} className="px-4 py-3 text-center">
          <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400 mx-auto">
            -
          </div>
        </td>
      );
    }

    const proofImageUrl = extractImageUrls(submission.proofData)[0];

    if (submission.isApproved) {
      return (
        <td key={step.id} className="px-4 py-3 text-center">
          <div
            className={`w-20 h-20 rounded relative mx-auto overflow-hidden ${
              proofImageUrl ? "cursor-zoom-in" : ""
            }`}
            onClick={() => proofImageUrl && setSelectedImage(proofImageUrl)}
          >
            <div className="absolute inset-0 bg-gray-200" />

            {proofImageUrl && (
              <img
                src={proofImageUrl}
                alt="인증 이미지"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}

            <div className="absolute inset-0 bg-black/45 pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-9 h-9 rounded-full bg-white/85 flex items-center justify-center shadow-sm">
                <svg
                  className="w-6 h-6 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </td>
      );
    }

    if (submission.isSubmitted) {
      return (
        <td key={step.id} className="px-4 py-3 text-center">
          <div
            className={`w-20 h-20 rounded relative mx-auto overflow-hidden ${
              proofImageUrl ? "cursor-zoom-in" : ""
            }`}
            onClick={() => proofImageUrl && setSelectedImage(proofImageUrl)}
          >
            <div className="absolute inset-0 bg-gray-200" />

            {proofImageUrl && (
              <img
                src={proofImageUrl}
                alt="인증 이미지"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )}

            <div className="absolute inset-0 bg-black/30 pointer-events-none" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="px-2 py-1 rounded-full bg-white/85 text-gray-800 text-xs font-semibold">
                대기중
              </span>
            </div>
          </div>
        </td>
      );
    }

    return (
      <td key={step.id} className="px-4 py-3 text-center">
        <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400 mx-auto">
          -
        </div>
      </td>
    );
  };

  return (
    <div className="space-y-6">
      {/* 캠페인 상세 요약 */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-60">
            <div className="w-full aspect-square bg-gray-100 rounded-xl overflow-hidden">
              {campaign.productImageUrl ? (
                <img
                  src={campaign.productImageUrl}
                  alt={campaign.productName}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                  이미지 없음
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-900 truncate">{campaign.productName}</h1>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                  <span>{campaign.channel}</span>
                  <span className="text-gray-300">·</span>
                  <span>{campaign.brand.name}</span>
                  {firstRunStartAt && lastRunEndAt && (
                    <>
                      <span className="text-gray-300">·</span>
                      <span>
                        {formatDate(firstRunStartAt)} ~ {formatDate(lastRunEndAt)}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <span
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    campaign.isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {campaign.isPublished ? "노출중" : "비노출"}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-5">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-500">원가</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {campaign.originalPrice.toLocaleString()}원
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-500">할인가</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {campaign.discountPrice.toLocaleString()}원
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-500">페이백</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {campaign.paybackPrice.toLocaleString()}원
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-500">참여</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {totalParticipants.toLocaleString()}명
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="text-xs font-semibold text-gray-500">회차</div>
                <div className="mt-1 text-lg font-bold text-gray-900">
                  {activeRunCount}회
                </div>
              </div>
            </div>

            {activeSortedRuns.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-semibold text-gray-500 mb-2">회차 일자</div>
                <div className="flex flex-wrap gap-2">
                  {activeSortedRuns.map((run) => {
                    const start = new Date(run.startAt);
                    const end = new Date(run.endAt);
                    const isSameDay = formatDate(start) === formatDate(end);
                    const label = isSameDay
                      ? formatDateWithWeekday(start)
                      : `${formatDateWithWeekday(start)} ~ ${formatDateWithWeekday(end)}`;

                    return (
                      <span
                        key={run.id}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          run.isActive
                            ? "bg-green-50 text-green-700 border-green-200"
                            : "bg-white text-gray-700 border-gray-200"
                        }`}
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {sortedSteps.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-semibold text-gray-500 mb-2">미션 단계</div>
                <div className="flex flex-wrap gap-2">
                  {sortedSteps.map((s) => (
                    <span
                      key={s.id}
                      className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold"
                    >
                      {s.stepOrder}. {s.title || s.type}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* 인증 테이블 */}
        {/* 테이블 */}
        {users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">참여자가 없습니다.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    UUID
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    휴대폰 번호
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    사는곳
                  </th>
                  {sortedSteps.map((step) => (
                    <th
                      key={step.id}
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {step.title || `인증단계 ${step.stepOrder}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((userGroup) => {
                  const participation =
                    userGroup.participations[userGroup.participations.length - 1];
                  const user = userGroup.user;

                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.uuid || "-"}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name || `유저 ${user.id}`}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{user.phone || "-"}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatAddress(user)}</div>
                      </td>
                      {sortedSteps.map((step) => renderCertificationStatus(participation, step))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 인증 이미지 확대 모달 */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition-all z-10"
              aria-label="닫기"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={selectedImage}
              alt="확대 이미지"
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg mx-auto"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandDashboardCampaignDetail;
